import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Shield, AlertTriangle, Loader2, Save, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { safeLog } from '@/utils/safeLogger';

interface PromptVersion {
  id: string;
  version_number: number;
  description?: string;
  system_prompt?: string;
  user_prompt_template?: string;
  is_active_for_prod?: boolean;
  is_active?: boolean;
  created_at: string;
  avg_completeness?: number;
  total_extractions?: number;
  parent_version_id?: string;
  optimization_notes?: any;
  isNew?: boolean;
}

export function PromptVersionManager() {
  const [promptType, setPromptType] = useState<'azure_extraction' | 'gemini_research'>('azure_extraction');
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [activeVersion, setActiveVersion] = useState<PromptVersion | null>(null);
  const [editingVersion, setEditingVersion] = useState<Partial<PromptVersion> | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadVersions();
  }, [promptType]);

  const loadVersions = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('prompt_versions')
        .select('*')
        .eq('prompt_type', promptType)
        .order('version_number', { ascending: false });

      if (error) throw error;

      setVersions(data || []);
      setActiveVersion(data?.find(v => v.is_active_for_prod) || null);
    } catch (error) {
      safeLog.error('[PromptVersionManager] Load error', error as Error);
      toast.error('Failed to load prompt versions');
    } finally {
      setIsLoading(false);
    }
  };

  const activateVersion = async (versionId: string) => {
    setIsLoading(true);
    try {
      // Deactivate all others of the same type
      await supabase
        .from('prompt_versions')
        .update({ is_active_for_prod: false })
        .eq('prompt_type', promptType)
        .neq('id', versionId);

      // Activate selected
      const { error } = await supabase
        .from('prompt_versions')
        .update({ is_active_for_prod: true })
        .eq('id', versionId);

      if (error) throw error;

      toast.success('✅ Prompt version activated for production');
      loadVersions();
    } catch (error) {
      safeLog.error('[PromptVersionManager] Activation error', error as Error);
      toast.error('Failed to activate version');
    } finally {
      setIsLoading(false);
    }
  };

  const savePromptVersion = async () => {
    if (!editingVersion) return;

    setIsSaving(true);
    try {
      // ✅ ALWAYS create new version (never overwrite)
      const maxVersion = Math.max(...versions.map(v => v.version_number), 0);
      
      let description = editingVersion.description || `Version ${maxVersion + 1}`;
      
      // If editing an existing version, mark it as edited
      if (!editingVersion.isNew && editingVersion.version_number !== undefined) {
        description = `[Edited] ${editingVersion.description || `v${editingVersion.version_number}`}`;
      }
      
      const { error } = await supabase
        .from('prompt_versions')
        .insert([{
          version_number: maxVersion + 1,
          prompt_type: promptType,
          description: description,
          system_prompt: editingVersion.system_prompt || '',
          user_prompt_template: editingVersion.user_prompt_template || '',
          parent_version_id: editingVersion.isNew 
            ? (editingVersion.parent_version_id || null)
            : (editingVersion.id || null), // Set original as parent when cloning
          is_active_for_prod: false,
          created_by: (await supabase.auth.getUser()).data.user?.id,
        }]);

      if (error) throw error;
      toast.success(`✅ New prompt version ${maxVersion + 1} created`);

      setEditingVersion(null);
      loadVersions();
    } catch (error) {
      safeLog.error('[PromptVersionManager] Save error', error as Error);
      toast.error('Failed to save prompt version');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading && versions.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Prompt Version Manager</CardTitle>
          <CardDescription>
            Manage prompts for Azure Extraction and Gemini Research separately
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={promptType} onValueChange={(val) => setPromptType(val as 'azure_extraction' | 'gemini_research')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="azure_extraction">
                <Shield className="w-4 h-4 mr-2" />
                Azure Extraction
              </TabsTrigger>
              <TabsTrigger value="gemini_research">
                <AlertTriangle className="w-4 h-4 mr-2" />
                Gemini Research
              </TabsTrigger>
            </TabsList>

            <TabsContent value={promptType} className="space-y-4 mt-4">
              <Card className="app-tile">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="app-icon-box">
                      <Shield className="h-5 w-5" />
                    </div>
                    <div>
                      <CardTitle className="app-section-title">Active Production Prompt</CardTitle>
                      <CardDescription>
                        Currently used for production {promptType === 'azure_extraction' ? 'extractions' : 'research'}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
          {activeVersion ? (
            <Alert className="border-success/40 bg-success/5">
              <Shield className="h-4 w-4 text-success" />
              <AlertTitle>Version {activeVersion.version_number} (Active)</AlertTitle>
              <AlertDescription>
                {activeVersion.description && <p className="mb-2">{activeVersion.description}</p>}
                Created: {new Date(activeVersion.created_at).toLocaleDateString()}
                <br />
                {activeVersion.avg_completeness && (
                  <>Avg Completeness: {(activeVersion.avg_completeness * 100).toFixed(1)}%<br /></>
                )}
                {activeVersion.total_extractions !== undefined && (
                  <>Total Extractions: {activeVersion.total_extractions}</>
                )}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-warning/40 bg-warning/5">
              <AlertTriangle className="h-4 w-4 text-warning" />
              <AlertTitle>Using Production Fallback</AlertTitle>
              <AlertDescription>
                No version activated yet. System is using current production prompts from <code className="text-xs bg-muted px-1 py-0.5 rounded">analyze-signature</code>.
                <br />
                Activate a version below to override with custom prompts.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card className="app-tile">
        <CardHeader>
          <CardTitle className="app-section-title">All Prompt Versions</CardTitle>
          <CardDescription>
            Manage and activate different prompt versions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-4">
              {versions.map(v => (
                <Card key={v.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          Version {v.version_number}
                          {v.is_active_for_prod && (
                            <Badge className="bg-success text-success-foreground hover:bg-success/90">PROD ACTIVE</Badge>
                          )}
                          {v.is_active && !v.is_active_for_prod && (
                            <Badge className="bg-primary text-primary-foreground hover:bg-primary/90">TEST ACTIVE</Badge>
                          )}
                        </CardTitle>
                        <CardDescription>
                          {new Date(v.created_at).toLocaleDateString()}
                          {v.total_extractions !== undefined && (
                            <> • {v.total_extractions} extractions</>
                          )}
                          {v.avg_completeness !== undefined && (
                            <> • {(v.avg_completeness * 100).toFixed(1)}% avg</>
                          )}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        {!v.is_active_for_prod && (
                          <Button 
                            size="sm" 
                            onClick={() => activateVersion(v.id)}
                            variant="default"
                            disabled={isLoading}
                          >
                            <Shield className="h-4 w-4 mr-2" />
                            Activate for Prod
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => setEditingVersion({
                            ...v,
                            isNew: false,
                            id: v.id
                          })}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Clone & Edit
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  {(v.description || v.optimization_notes?.summary) && (
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        {v.description || v.optimization_notes?.summary}
                      </p>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Create New Version</CardTitle>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setEditingVersion({ isNew: true })}>
            + New Prompt Version
          </Button>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={!!editingVersion} onOpenChange={() => setEditingVersion(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingVersion?.isNew 
                ? 'Create New Version' 
                : `Clone Version ${editingVersion?.version_number} (creates v${Math.max(...versions.map(v => v.version_number), 0) + 1})`
              }
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="system" className="w-full">
            <TabsList>
              <TabsTrigger value="system">System Prompt</TabsTrigger>
              <TabsTrigger value="user">User Prompt Template</TabsTrigger>
              <TabsTrigger value="metadata">Metadata</TabsTrigger>
            </TabsList>
            
            <TabsContent value="system" className="space-y-4">
              <div>
                <Label>System Prompt</Label>
                <Textarea
                  value={editingVersion?.system_prompt || ''}
                  onChange={(e) => setEditingVersion({
                    ...editingVersion,
                    system_prompt: e.target.value
                  })}
                  rows={20}
                  className="font-mono text-sm"
                  placeholder="Enter system prompt..."
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Available template variables: {'{{userEmail}}'}, {'{{senderEmail}}'}, {'{{requiredFieldsJson}}'}
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="user" className="space-y-4">
              <div>
                <Label>User Prompt Template</Label>
                <Textarea
                  value={editingVersion?.user_prompt_template || ''}
                  onChange={(e) => setEditingVersion({
                    ...editingVersion,
                    user_prompt_template: e.target.value
                  })}
                  rows={15}
                  className="font-mono text-sm"
                  placeholder="Enter user prompt template..."
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Available template variables: {'{{userEmail}}'}, {'{{senderEmail}}'}, {'{{rawText}}'}
                </p>
              </div>
            </TabsContent>
            
            <TabsContent value="metadata" className="space-y-4">
              <div>
                <Label>Description</Label>
                <Input
                  value={editingVersion?.description || ''}
                  onChange={(e) => setEditingVersion({
                    ...editingVersion,
                    description: e.target.value
                  })}
                  placeholder="e.g., Improved company name detection from logos"
                />
              </div>
              
              {editingVersion?.isNew && (
                <div>
                  <Label>Parent Version (optional)</Label>
                  <Select
                    value={editingVersion?.parent_version_id || undefined}
                    onValueChange={(val) => setEditingVersion({
                      ...editingVersion,
                      parent_version_id: val
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select parent version (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {versions.map(v => (
                        <SelectItem key={v.id} value={v.id}>
                          Version {v.version_number} - {v.description || 'No description'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingVersion(null)}>
              Cancel
            </Button>
            <Button onClick={savePromptVersion} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Save className="h-4 w-4 mr-2" />
              {editingVersion?.isNew ? 'Create Version' : 'Save as New Version'}
            </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
